import type { Tone } from '@/theme/tokens';
import type { ShellJob, ShellJobCounts, ShellJobItem, ShellJobItemStatus, ShellJobStatus } from './shell-job.service';
import { formatInstant } from './run-timeline.ts';
import { typeLabel } from './utils.ts';

// ── Item results ──

export interface ResultMeta {
  label: string;
  tone: Tone;
}

export const ITEM_RESULT: Record<ShellJobItemStatus, ResultMeta> = {
  ok: { label: 'OK', tone: 'success' },
  exit_nonzero: { label: 'Exit ≠ 0', tone: 'error' },
  timeout: { label: 'Sent · no answer', tone: 'warning' },
  failed: { label: 'Failed to send', tone: 'error' },
  offline: { label: 'Skipped · offline', tone: 'neutral' },
  not_enrolled: { label: 'Not enrolled', tone: 'neutral' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  pending: { label: 'Queued', tone: 'info' },
  running: { label: 'Sent · waiting for answer', tone: 'info' },
  waiting: { label: 'Waiting for device', tone: 'warning' },
  expired: { label: 'Expired · not sent', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'info' },
};

/** A run that waits for no answer: send and forget (timeout_ms 0, not send-only). */
export const isNoTimeout = (job: Pick<ShellJob, 'timeout_ms' | 'send_only'>) => job.timeout_ms === 0 && !job.send_only;

/**
 * The chip text for one item — exit codes are spelled out. In a no-timeout run a delivered device reads "Sent OK",
 * answered or not, and while the run is open an offline or not-enrolled device still gets the command when it can.
 */
export function itemResultLabel(item: Pick<ShellJobItem, 'status' | 'exit_code'>, noTimeout = false, open = false): string {
  if (noTimeout && (item.status === 'sent' || item.status === 'ok')) return 'Sent OK';
  if (noTimeout && open && item.status === 'offline') return 'Offline · runs when back';
  if (noTimeout && open && item.status === 'not_enrolled') return 'Not enrolled · runs when enrolled';
  if (item.status === 'exit_nonzero' && item.exit_code !== undefined) return `Exit ${item.exit_code}`;
  return ITEM_RESULT[item.status].label;
}

export const itemTone = (item: Pick<ShellJobItem, 'status'>, noTimeout = false): Tone =>
  noTimeout && (item.status === 'sent' || item.status === 'ok') ? 'success' : ITEM_RESULT[item.status].tone;

/** What a device's "Answered" says: when it answered, or why there is no answer. */
export function answered(item: ShellJobItem, timezone: string, nowMs: number, noTimeout = false): string {
  if (noTimeout && item.status === 'sent') return 'Not yet';
  switch (item.status) {
    case 'running':
      return 'Waiting for answer';
    case 'timeout':
      return 'No answer';
    case 'failed':
      return 'Not delivered';
    case 'sent':
      return 'Not collected (send only)';
  }
  return item.finished_at ? formatInstant(item.finished_at, timezone, nowMs) : '—';
}

/**
 * A run is Active while it can still send and Inactive once it cannot; the
 * hint is the exact state, shown on hover.
 */
export function runActivity(job: Pick<ShellJob, 'status' | 'timeout_ms' | 'send_only' | 'target'>): { label: 'Active' | 'Inactive'; tone: Tone; hint: string } {
  const noTimeout = isNoTimeout(job);
  const active = (hint: string) => ({ label: 'Active' as const, tone: 'info' as const, hint });
  const inactive = (hint: string) => ({ label: 'Inactive' as const, tone: 'neutral' as const, hint });
  switch (job.status) {
    case 'running':
      return active(noTimeout ? 'Sending' : 'Running');
    case 'waiting':
      return active(noTimeout || job.target !== 'list' ? 'Open until the deadline' : 'Waiting for devices until the deadline');
    case 'paused':
      return active('Paused: waiting for you to continue');
    case 'completed':
      return inactive(noTimeout ? 'Sent to every device' : 'Completed');
    case 'cancelled':
      return inactive('Cancelled');
    default:
      return inactive('Interrupted');
  }
}

/** Badge for a run's status. */
export const JOB_STATUS: Record<ShellJobStatus, ResultMeta> = {
  running: { label: 'Running', tone: 'info' },
  waiting: { label: 'Waiting for devices', tone: 'warning' },
  paused: { label: 'Paused', tone: 'error' },
  completed: { label: 'Completed', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  interrupted: { label: 'Interrupted', tone: 'warning' },
};

/** The run's deadline has passed: nothing more is sent; Continue and Retry are off. */
export const pastDeadline = (job: Pick<ShellJob, 'expires_at'>, nowMs: number) =>
  !!job.expires_at && Date.parse(job.expires_at) <= nowMs;

// ── Status filter pills ──

export type ResultFilter =
  | 'all' | 'problems' | 'ok' | 'sent' | 'sent_ok' | 'exit_nonzero' | 'timeout' | 'failed' | 'waiting' | 'not_done' | 'skipped' | 'cancelled';

export const RESULT_FILTER_STATUSES: Record<Exclude<ResultFilter, 'all'>, ShellJobItemStatus[]> = {
  problems: ['exit_nonzero', 'timeout', 'failed'],
  ok: ['ok'],
  sent: ['sent'],
  // No-timeout runs: delivered, answered or not.
  sent_ok: ['sent', 'ok'],
  exit_nonzero: ['exit_nonzero'],
  timeout: ['timeout'],
  failed: ['failed'],
  waiting: ['pending', 'running', 'waiting'],
  // Everything that is not OK, not a problem and not sent.
  not_done: ['pending', 'running', 'waiting', 'offline', 'not_enrolled', 'expired', 'cancelled'],
  skipped: ['offline', 'not_enrolled', 'expired'],
  cancelled: ['cancelled'],
};

/** The filter chips: a no-timeout run has nothing "not done", only sent, failed or queued. */
export function filterChips(noTimeout: boolean): { key: ResultFilter; label: string }[] {
  return noTimeout
    ? [{ key: 'all', label: 'All' }, { key: 'problems', label: 'Problems' }, { key: 'sent_ok', label: 'Sent OK' }]
    : [{ key: 'all', label: 'All' }, { key: 'problems', label: 'Problems' }, { key: 'ok', label: 'OK' }, { key: 'not_done', label: 'Not done' }];
}

export function countFor(c: ShellJobCounts, f: ResultFilter): number {
  if (f === 'all') return c.total;
  return RESULT_FILTER_STATUSES[f].reduce((n, s) => n + c[s], 0);
}

// ── Job maths ──

export const problemCount = (c: ShellJobCounts) => c.exit_nonzero + c.timeout + c.failed;
/** Successful for progress: answered OK, or (send only) delivered. */
export const okCount = (c: ShellJobCounts) => c.ok + c.sent;

// Rough per-batch time for the "about N min" estimates. Most commands answer
// in well under a second; the round trip through MQTT dominates.
const SECONDS_PER_BATCH = 3;

/** "about 2 min" / "about 40 s" for `devices` run `concurrency` at a time. */
export function paceEstimate(devices: number, concurrency: number): string {
  if (devices <= 0) return '—';
  return `about ${runDuration(Math.ceil(devices / Math.max(1, concurrency)) * SECONDS_PER_BATCH * 1000)}`;
}

/**
 * Compact duration: "850 ms", "4.2 s", "42 s", "2 min", "3 h 05 min", "2 d 3 h".
 * Below an hour, `withSeconds` makes the minutes "2 min 04 s".
 */
/** "all sent in about 4 min": n devices go out per slot release, so the last batch leaves after (batches − 1) waits. */
export function allSentIn(devices: number, concurrency: number, slotMs: number): string {
  const waits = Math.ceil(devices / Math.max(1, concurrency)) - 1;
  return waits <= 0 ? 'all sent right away' : `all sent in about ${runDuration(waits * slotMs)}`;
}

export function shortDuration(ms: number, withSeconds = false): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  // Each tier is chosen from the value already rounded to its own unit, and
  // minutes are split from whole seconds, so rounding up can never leave
  // "1000 ms", "60 s", "1 min 60 s", "60 min" or "24 h".
  const whole = Math.round(ms);
  if (whole < 1000) return `${whole} ms`;
  const tenths = Math.round(ms / 100);
  if (tenths < 100) return `${(tenths / 10).toFixed(1)} s`;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs} s`;
  if (withSeconds && secs < 3600) return `${Math.floor(secs / 60)} min ${String(secs % 60).padStart(2, '0')} s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min`;
  if (mins < 1440) return `${Math.floor(mins / 60)} h ${String(mins % 60).padStart(2, '0')} min`;
  const hours = Math.round(mins / 60);
  return `${Math.floor(hours / 24)} d ${hours % 24} h`;
}

/** A run's length in its largest unit, as the runs list shows it: "48 s", "3 min", "5 h", "2 days". */
export function runDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  // Each tier rounds the value already rounded to the tier below, so 59.6 s
  // reads "1 min", never "60 s".
  const secs = Math.round(ms / 1000);
  if (secs < 60) return secs < 1 ? '< 1 s' : `${secs} s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.round(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

export function itemDuration(item: ShellJobItem): string {
  if (!item.started_at || !item.finished_at) return '—';
  return shortDuration(new Date(item.finished_at).getTime() - new Date(item.started_at).getTime());
}

/** Primary device label — the machine id the app identifies devices by. */
export const deviceLabel = (item: Pick<ShellJobItem, 'machine_id' | 'device_number' | 'serial_number' | 'device_id'>) =>
  item.machine_id || item.device_number || item.serial_number || item.device_id;

// A number or serial that only repeats the machine id says nothing new (some
// devices carry their machine id as their number).
const notMachineId = (value: string, machineId: string) => !!value && value.toLowerCase() !== machineId.toLowerCase();

/** The line under a device row: "R-10021 · Aviron Strong Series · Rower". The serial is in the device panel. */
export function deviceRowLabel(item: Pick<ShellJobItem, 'machine_id' | 'device_number' | 'model' | 'device_type'>): string {
  // Without a machine id the number already is the row's title.
  const number = item.machine_id && notMachineId(item.device_number, item.machine_id) ? item.device_number : '';
  return [number, item.model, item.device_type && typeLabel(item.device_type)].filter(Boolean).join(' · ');
}

/** Secondary line under the machine id in the device panel: "R-10021 · SN 8F2K-11A9". */
export function deviceSubLabel(item: Pick<ShellJobItem, 'machine_id' | 'device_number' | 'serial_number'>): string {
  // Without a machine id the number already is the primary label.
  const number = item.machine_id && notMachineId(item.device_number, item.machine_id) ? item.device_number : '';
  const serial = notMachineId(item.serial_number, item.machine_id) ? `SN ${item.serial_number}` : '';
  return [number, serial].filter(Boolean).join(' · ');
}

/**
 * Still doing something: running its online pass, or (kept open) waiting
 * for offline devices. Both poll, both can be cancelled.
 */
export const isActiveJob = (job: Pick<ShellJob, 'status'>) => job.status === 'running' || job.status === 'waiting';

/** The server's reconcile looks back this far for answers (ListShellJobAnswers). */
const RECONCILE_WINDOW_MS = 30 * 24 * 3_600_000;

/** A no-timeout run with devices that have not answered yet: their answers still come in, even once it is Inactive. */
export const awaitsAnswers = (job: Pick<ShellJob, 'timeout_ms' | 'send_only' | 'counts' | 'started_at'>, nowMs = Date.now()) =>
  isNoTimeout(job) && job.counts.sent > 0 && (!job.started_at || nowMs - Date.parse(job.started_at) < RECONCILE_WINDOW_MS);

/** How often a job page refreshes: fast while active, slowly while paused or while a no-timeout run's answers come in. */
export const jobPollInterval = (
  job: Pick<ShellJob, 'status' | 'timeout_ms' | 'send_only' | 'counts' | 'started_at'>,
  nowMs = Date.now(),
): number | false => (isActiveJob(job) ? 2_000 : job.status === 'paused' || awaitsAnswers(job, nowMs) ? 10_000 : false);

// ── CSV ──

const csvCell = (v: string | number | undefined) => {
  const s = v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function itemsToCSV(items: ShellJobItem[]): string {
  const header = ['machine_id', 'device_number', 'device_id', 'status', 'exit_code', 'stdout', 'stderr', 'error', 'started_at', 'finished_at'];
  const rows = items.map((i) =>
    [i.machine_id, i.device_number, i.device_id, i.status, i.exit_code, i.stdout, i.stderr, i.error, i.started_at, i.finished_at]
      .map(csvCell)
      .join(','),
  );
  return [header.join(','), ...rows].join('\n');
}
